import { Image } from 'expo-image';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Modal, Platform, StyleSheet, View } from 'react-native';

import { claimIcon } from '@/assets/images/icons';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import GradientCircularButton from '../buttons/GradientCircularButton';
import Text from '../Text';
import ModalCard from './ModalCard';
import { ValidateWikiChangesData, validateWikiChangesResolver } from '@/lib/validations/ValidateWikiChangesSchema';
import TextInputField from '../inputs/TextInputField';
import PhoneInput from '../inputs/PhoneInput';
import { useWikiChangesNpoModal } from '@/contexts/wikiChangesNpoModalContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useSaveWikiTentativeData } from '@/api/hooks/nonProfitOrgHooks';
import FormWrapper from '../forms/FormWrapper';

const WikiChangeNpoModal = () => {
  const {
    state: { visible, npoId },
    setVisible
  } = useWikiChangesNpoModal();

  const { showSuccess } = useEndFlowModal();

  const { mutateAsync: saveWikiTentativeData, isPending } = useSaveWikiTentativeData();

  const {
    control,
    handleSubmit,
    setFocus,
    setValue,
    trigger,
    reset,
    resetField,
    setError,
    formState: { errors, isValid, submitCount }
  } = useForm<ValidateWikiChangesData>({
    mode: 'onChange',
    resolver: validateWikiChangesResolver as any
  });

  const onValidate: SubmitHandler<ValidateWikiChangesData> = async (data) => {
    if (data.emailAddress || data.phone) {
      saveWikiTentativeData(
        {
          npoId: npoId,
          email: data.emailAddress ?? '',
          phone: data.phone ?? ''
        },
        {
          onSuccess: () => {
            showSuccess({ title: i18n.t('end_flow.success_title'), message: i18n.t('wikiChangeNpoModal.success') });
            reset();
            onCloseModal();
          }
        }
      );
    } else {
      onCloseModal();
    }
  };

  const onCloseModal = () => setVisible(false);

  const renderModalCard = () => (
    <ModalCard closeModal={onCloseModal} showCloseButton closeOnTapOutside>
      <View style={styles.cardContainer}>
        {Platform.OS === 'web' && (
          <GradientCircularButton disabled size={92}>
            <Image style={styles.icon} source={claimIcon} contentFit={'contain'} />
          </GradientCircularButton>
        )}
        <Text style={styles.titleText} fontWeight="700">
          {i18n.t('wikiChangeNpoModal.title')}
        </Text>
        <Text style={styles.descriptionText}>{i18n.t('wikiChangeNpoModal.description')}</Text>

        <View style={styles.input}>
          <TextInputField
            name="emailAddress"
            control={control}
            placeholder={i18n.t('wikiChangeNpoModal.email_placeholder')}
            type="email-address"
            returnKeyLabel="next"
            returnKeyType="next"
            label={i18n.t('wikiChangeNpoModal.email_label')}
            onSubmitEditing={() => setFocus('phone')}
          />
        </View>

        <View style={styles.phoneInputContainer}>
          <PhoneInput
            label={i18n.t('wikiChangeNpoModal.phone_label')}
            placeholder={i18n.t('wikiChangeNpoModal.phone_placeholder')}
            error={errors.phone}
            onNumberChange={(number) => {
              if (number) {
                const fullNumber = `+${number?.getCountryCode()}${number?.getNationalNumber()}`;
                setValue('phone', fullNumber);
                trigger('phone');
              } else {
                resetField('phone');
                setError('phone', {
                  type: 'required',
                  message: i18n.t('mix.valid_phone_number')
                });
              }
            }}
          />
        </View>
      </View>

      <BrandedButton
        textProps={{ ellipsizeMode: 'middle', numberOfLines: 2 }}
        disabled={!isValid && submitCount > 0}
        loading={isPending}
        textStyles={styles.buttonTextStyle}
        backGroundColor="#FE9E22"
        style={styles.button}
        onPress={handleSubmit(onValidate)}>
        {i18n.t('mix.submit')}
      </BrandedButton>
    </ModalCard>
  );

  return (
    <Modal animationType="fade" transparent={true} visible={visible} statusBarTranslucent onRequestClose={onCloseModal}>
      {Platform.OS === 'web' ? (
        renderModalCard()
      ) : (
        <FormWrapper horizontalPadding={0} bounces={false}>
          {renderModalCard()}
        </FormWrapper>
      )}
    </Modal>
  );
};

const styles = StyleSheet.create({
  cardContainer: {
    width: '100%',
    backgroundColor: 'white',
    flexDirection: 'column',
    borderRadius: 20,
    paddingHorizontal: 8,
    paddingBottom: 16,
    paddingTop: 20,
    alignItems: 'center',
    justifyContent: 'center',
    gap: Platform.OS === 'web' ? 15 : 5
  },

  titleText: {
    fontSize: 24,
    marginBottom: Platform.OS === 'web' ? 16 : 8,
    textAlign: 'center',
    color: 'black'
  },

  descriptionText: {
    fontSize: 16,
    marginBottom: Platform.OS === 'web' ? 12 : 4,
    textAlign: 'center'
  },

  icon: {
    width: 48,
    height: 48
  },

  input: {
    width: '100%'
  },

  button: {
    marginTop: 15,
    width: '100%'
  },

  buttonTextStyle: {
    alignSelf: 'center',
    textAlign: 'center',
    textAlignVertical: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 10
  },

  phoneInputContainer: {
    width: '100%',
    zIndex: 10
  }
});

export default WikiChangeNpoModal;
